// TODO: This file was updated by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  SORT_KEY,
  CRISPRAnalysis,
  CRISPRAnalysisSummary,
  GuideGroupSort,
  CRISPRRegion,
} from 'constants/CRISPRConstants';
import {EventConstants} from 'constants/EventConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceVersion} from 'constants/SequenceConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';

export interface Guide {
  // Unclear if this should always be defined or not. Need to type more files to confirm.
  regionId?: string;
  position: number;
  strand: StrandOfDoubleStranded | null;
}

class CRISPRAnalysisStore extends Store {
  analyses!: {[analysisId: string]: CRISPRAnalysisSummary};
  cidIdMap!: {[cid: string]: string};
  selectedRegionsMap!: {[analysisId: string]: {[regionId: string]: boolean}};
  guideGroupSortMap!: {[analysisId: string]: GuideGroupSort};
  selectedGuideGroupsMap!: {[analysisId: string]: {[guideGroupKey: string]: boolean}};
  originalSequenceVersionMap!: {[analysisId: string]: SequenceVersion};
  analysisLoadingXHRCount!: number;
  regionLoadingXHRCount!: number;
  // In-flight bulk region-update batches, keyed by batch id. Removal by id is idempotent, so the
  // loading state can't go negative or leak if a settle is dispatched twice or for an unknown id.
  inFlightBulkUpdateRegionBatchIds!: Set<string>;
  regeneratingAnalysisXHRCount!: number;

  override resetState(): void {
    this.analyses = {};
    this.cidIdMap = {};
    this.selectedRegionsMap = {};
    this.guideGroupSortMap = {};
    this.selectedGuideGroupsMap = {};
    this.originalSequenceVersionMap = {};
    this.analysisLoadingXHRCount = 0;
    this.regionLoadingXHRCount = 0;
    this.inFlightBulkUpdateRegionBatchIds = new Set();
    this.regeneratingAnalysisXHRCount = 0;
  }

  getState(): {
    analyses: {[analysisId: string]: CRISPRAnalysisSummary};
    cidIdMap: {[cid: string]: string};
    selectedRegionsMap: {[analysisId: string]: {[regionId: string]: boolean}};
    guideGroupSortMap: {[analysisId: string]: GuideGroupSort};
    selectedGuideGroupsMap: {[analysisId: string]: {[guideGroupKey: string]: boolean}};
    originalSequenceVersionMap: {[analysisId: string]: SequenceVersion};
    analysisLoading: boolean;
    regionLoading: boolean;
    regeneratingAnalysis: boolean;
  } {
    return {
      analyses: this.analyses,
      cidIdMap: this.cidIdMap,
      selectedRegionsMap: this.selectedRegionsMap,
      guideGroupSortMap: this.guideGroupSortMap,
      selectedGuideGroupsMap: this.selectedGuideGroupsMap,
      originalSequenceVersionMap: this.originalSequenceVersionMap,
      analysisLoading: this.analysisLoadingXHRCount > 0,
      regionLoading: this.regionLoadingXHRCount > 0 || this.inFlightBulkUpdateRegionBatchIds.size > 0,
      regeneratingAnalysis: this.regeneratingAnalysisXHRCount > 0,
    };
  }

  getAnalysesForSequence(sequenceId: string): Array<CRISPRAnalysisSummary> {
    // eslint-disable-next-line no-restricted-syntax
    return _.values(this.analyses).filter((a) => a.sequenceId === sequenceId);
  }

  guideGroupKey(guideGroup: Array<Guide>): string {
    return guideGroup.map((guide) => `${guide.regionId}-${guide.position}-${guide.strand}`).join('-');
  }

  isFullAnalysis(analysis: CRISPRAnalysis | CRISPRAnalysisSummary): analysis is CRISPRAnalysis {
    // @ts-expect-error
    return analysis != null && analysis.regions != null;
  }

  isFullAnalysisId(analysisId: string): boolean {
    return this.isFullAnalysis(this.analyses[analysisId]);
  }

  isGuideGroupSelected(
    selectedGuideGroups: {[guideGroupKey: string]: boolean},
    guideGroup: Array<Guide>
  ): boolean {
    return selectedGuideGroups[this.guideGroupKey(guideGroup)] || false;
  }

  _updateAnalysis(analysis: CRISPRAnalysisSummary): void {
    this.analyses = reactUpdate(this.analyses, _.object([[analysis.id, {$set: analysis}]]));
  }

  // Merge one or more updated regions (keyed by region id) into an analysis, then run the guide-group
  // cleanup that a region change requires. No-ops if the analysis is gone or there is nothing to merge,
  // so callers stay balanced even when the analysis was deleted mid-flight.
  _mergeUpdatedRegions(analysisId: string, updatedRegionsById: {[regionId: string]: CRISPRRegion}): void {
    const analysis = this.analyses[analysisId] as CRISPRAnalysis | undefined;
    const updatedRegionIds = Object.keys(updatedRegionsById);
    if (analysis == null || updatedRegionIds.length === 0) {
      return;
    }
    const regions = analysis.regions.map((r) => updatedRegionsById[r.id] ?? r);
    this._updateAnalysis(reactUpdate(analysis, {regions: {$set: regions}}));
    this._deselectContainedGuideGroups(analysisId, updatedRegionIds);
    this._resetOffset(analysisId);
  }

  _mergeAnalysis(analysis: CRISPRAnalysisSummary): void {
    if (this.analyses[analysis.id] != null) {
      this.analyses = reactUpdate(this.analyses, _.object([[analysis.id, {$merge: analysis}]]));
    } else {
      this._updateAnalysis(analysis);
    }
  }

  _updateSelectedRegions(analysisId: string, selectedRegions: {[regionId: string]: boolean}): void {
    this.selectedRegionsMap = reactUpdate(
      this.selectedRegionsMap,
      _.object([[analysisId, {$set: selectedRegions}]])
    );
  }

  _removeAnalysis(analysisId: string): void {
    this.analyses = _.clone(this.analyses);
    delete this.analyses[analysisId];
    this.selectedRegionsMap = _.clone(this.selectedRegionsMap);
    delete this.selectedRegionsMap[analysisId];
    this.cidIdMap = _.clone(this.cidIdMap);
    // eslint-disable-next-line no-restricted-syntax
    for (const analysisCid in this.cidIdMap) {
      if (this.cidIdMap[analysisCid] === analysisId) {
        delete this.cidIdMap[analysisCid];
      }
    }
  }

  _setDefaultSortAndSelectedGuideGroups(analysisId: string): void {
    this.guideGroupSortMap = reactUpdate(
      this.guideGroupSortMap,
      _.object([
        [
          analysisId,
          {
            $set: {
              offset: 0,
              key: SORT_KEY.POSITION,
              reverse: false,
            },
          },
        ],
      ])
    );
    this.selectedGuideGroupsMap = reactUpdate(
      this.selectedGuideGroupsMap,
      _.object([[analysisId, {$set: {}}]])
    );
  }

  _updateGuideGroupSort(analysisId: string, sort: Partial<GuideGroupSort>): void {
    this.guideGroupSortMap = reactUpdate(this.guideGroupSortMap, _.object([[analysisId, {$merge: sort}]]));
  }

  _resetOffset(analysisId: string): void {
    this._updateGuideGroupSort(analysisId, {offset: 0});
  }

  _toggleSelectedGuideGroups(analysisId: string, guideGroups: Array<Array<Guide>>, selected: boolean): void {
    const selectedGuideGroups = _.clone(this.selectedGuideGroupsMap[analysisId]);
    for (const guideGroup of Array.from(guideGroups)) {
      if (selected) {
        selectedGuideGroups[this.guideGroupKey(guideGroup)] = true;
      } else {
        delete selectedGuideGroups[this.guideGroupKey(guideGroup)];
      }
    }
    this.selectedGuideGroupsMap = reactUpdate(
      this.selectedGuideGroupsMap,
      _.object([[analysisId, {$set: selectedGuideGroups}]])
    );
  }

  _deselectContainedGuideGroups(analysisId: string, regionIds: Array<string>): void {
    const shouldRemove = (guideGroupKey: string): boolean =>
      regionIds.some((regionId) => guideGroupKey.startsWith(regionId));
    const selectedGuideGroups = _.clone(this.selectedGuideGroupsMap[analysisId]);
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const guideGroupKey in selectedGuideGroups) {
      // @ts-expect-error unused constant
      const value = selectedGuideGroups[guideGroupKey];
      if (shouldRemove(guideGroupKey)) {
        delete selectedGuideGroups[guideGroupKey];
      }
    }
    this.selectedGuideGroupsMap = reactUpdate(
      this.selectedGuideGroupsMap,
      _.object([[analysisId, {$set: selectedGuideGroups}]])
    );
  }

  _updateOriginalSequenceVersion(analysisId: string, originalVersion: unknown): void {
    this.originalSequenceVersionMap = reactUpdate(
      this.originalSequenceVersionMap,
      _.object([[analysisId, {$set: originalVersion}]])
    );
  }

  override payloadHandler(action: FluxAction): true {
    // oxlint-disable-next-line default-case
    switch (action.actionType) {
      case EventConstants.CRISPR_ANALYSIS_CREATE:
      case EventConstants.CRISPR_ANALYSIS_OPEN:
      case EventConstants.CRISPR_ANALYSIS_UPDATE:
      case EventConstants.CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE:
      case EventConstants.CRISPR_ANALYSIS_DELETE:
        this.analysisLoadingXHRCount++;
        this.emitChange();
        break;

      case EventConstants.CRISPR_REGION_CREATE:
      case EventConstants.CRISPR_REGION_UPDATE:
      case EventConstants.CRISPR_REGION_DELETE:
        this.regionLoadingXHRCount++;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_CREATE_COMPLETED:
        this._updateAnalysis(action.analysis);
        this.cidIdMap = reactUpdate(
          this.cidIdMap,
          _.object([[action.analysisCid, {$set: action.analysisId}]])
        );
        this._updateSelectedRegions(
          action.analysisId,
          _.object(action.analysis.regions.map((r: CRISPRRegion) => [r.id, true]))
        );
        this._setDefaultSortAndSelectedGuideGroups(action.analysisId);
        this.analysisLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_OPEN_COMPLETED:
        this._updateAnalysis(action.analysis);
        this._updateSelectedRegions(
          action.analysisId,
          _.object(action.analysis.regions.map((r: CRISPRRegion) => [r.id, true]))
        );
        this._setDefaultSortAndSelectedGuideGroups(action.analysisId);
        this.analysisLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_UPDATE_COMPLETED:
        this._updateAnalysis(action.analysis);
        this.analysisLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_DELETE_COMPLETED:
        this.analysisLoadingXHRCount--;
        this._removeAnalysis(action.analysisId);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_TOGGLE_SELECTED_REGIONS: {
        const selectedRegions = _.clone(this.selectedRegionsMap[action.analysisId]);
        for (const regionId of Array.from(action.regionIds)) {
          if (action.selected) {
            selectedRegions[regionId as string] = true;
          } else {
            delete selectedRegions[regionId as string];
          }
        }
        if (!action.selected) {
          this._deselectContainedGuideGroups(action.analysisId, action.regionIds);
          this._resetOffset(action.analysisId);
        }
        this._updateSelectedRegions(action.analysisId, selectedRegions);
        this.emitChange();
        break;
      }

      case EventConstants.CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE_COMPLETED:
        for (const analysis of Array.from(action.analyses)) {
          this._mergeAnalysis(analysis as CRISPRAnalysisSummary);
        }
        this.analysisLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_REGION_CREATE_COMPLETED: {
        let analysis = this.analyses[action.analysisId] as CRISPRAnalysis;
        analysis = reactUpdate(analysis, {regions: {$push: [action.region]}});
        this._updateAnalysis(analysis);
        const selectedRegions = reactUpdate(
          this.selectedRegionsMap[action.analysisId],
          _.object([[action.regionId, {$set: true}]])
        );
        this._updateSelectedRegions(action.analysisId, selectedRegions);
        this.regionLoadingXHRCount--;
        this.emitChange();
        break;
      }

      case EventConstants.CRISPR_REGION_UPDATE_COMPLETED:
        this._mergeUpdatedRegions(action.analysisId, {[action.regionId]: action.region});
        this.regionLoadingXHRCount--;
        this.emitChange();
        break;

      // Bulk counterpart to CRISPR_REGION_UPDATE, dispatched once for an entire recompute/regenerate
      // batch instead of once per region so the whole batch incurs a single re-render. Loading is
      // tracked by batch id (not a counter) so it stays balanced even if a settle is dispatched twice
      // or for an unknown id.
      case EventConstants.CRISPR_REGIONS_BULK_UPDATE:
        this.inFlightBulkUpdateRegionBatchIds.add(action.batchId);
        this.emitChange();
        break;

      // Bulk counterpart to CRISPR_REGION_UPDATE_COMPLETED. Merges every updated region into the
      // analysis in one mutation and emits a single change. Always remove the batch id - including
      // when a region failed validation (absent from updatedRegions) or the request failed (empty
      // updatedRegions) - so loading stays balanced.
      case EventConstants.CRISPR_REGIONS_BULK_UPDATE_SETTLED:
        this.inFlightBulkUpdateRegionBatchIds.delete(action.batchId);
        this._mergeUpdatedRegions(
          action.analysisId,
          action.updatedRegions as {[regionId: string]: CRISPRRegion}
        );
        this.emitChange();
        break;

      case EventConstants.CRISPR_REGION_ANALYZE_OFF_TARGET_COMPLETED:
      case EventConstants.CRISPR_REGION_ANALYZE_ON_TARGET_COMPLETED: {
        let analysis = this.analyses[action.analysisId] as CRISPRAnalysis | undefined;
        if (analysis == null) {
          // Don't do anything if analysis gets deleted first
          // @ts-expect-error should return boolean
          return;
        }
        // eslint-disable-next-line no-restricted-syntax
        const oldRegion = _.find(analysis.regions, (r) => r.id === action.regionId);
        if (oldRegion == null) {
          // Don't do anything if region gets deleted first
          // @ts-expect-error should return boolean
          return;
        }
        assert(oldRegion.start === action.region.start, 'Server response should not change start of region');
        assert(oldRegion.end === action.region.end, 'Server response should not change end of region');
        // eslint-disable-next-line no-restricted-syntax
        const newGuides = _.map(oldRegion.guides, (oldGuide) => {
          const guideFromServer = _.findWhere(action.region.guides, {
            bases: oldGuide.bases,
            position: oldGuide.position,
            strand: oldGuide.strand,
          });
          assert(guideFromServer != null, 'Server response should not return new guides');
          return reactUpdate(oldGuide, {$merge: guideFromServer});
        });
        const newRegion = reactUpdate(oldRegion, {$merge: {guides: newGuides}});
        const newRegions = analysis.regions.map((r) => {
          if (r.id === action.regionId) {
            return newRegion;
          } else {
            return r;
          }
        });
        analysis = reactUpdate(analysis, {regions: {$set: newRegions}});
        this._updateAnalysis(analysis);
        this.emitChange();
        break;
      }

      case EventConstants.CRISPR_REGION_DELETE_COMPLETED: {
        let analysis = this.analyses[action.analysisId] as CRISPRAnalysis;
        const regions = analysis.regions.filter((r) => r.id !== action.regionId);
        analysis = reactUpdate(analysis, {regions: {$set: regions}});
        this._updateAnalysis(analysis);
        const selectedRegions = _.clone(this.selectedRegionsMap[action.analysisId]);
        delete selectedRegions[action.regionId];
        this._updateSelectedRegions(action.analysisId, selectedRegions);
        this._deselectContainedGuideGroups(action.analysisId, [action.regionId]);
        this._resetOffset(action.analysisId);
        this.regionLoadingXHRCount--;
        this.emitChange();
        break;
      }

      case EventConstants.CRISPR_ANALYSIS_UPDATE_GUIDE_GROUP_SORT:
        this._updateGuideGroupSort(action.analysisId, action.sort);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_TOGGLE_SELECTED_GUIDE_GROUPS:
        this._toggleSelectedGuideGroups(action.analysisId, action.guideGroups, action.selected);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_CREATE_FAILED:
      case EventConstants.CRISPR_ANALYSIS_OPEN_FAILED:
      case EventConstants.CRISPR_ANALYSIS_UPDATE_FAILED:
      case EventConstants.CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE_FAILED:
      case EventConstants.CRISPR_ANALYSIS_DELETE_FAILED:
        this.analysisLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_REGION_CREATE_FAILED:
      case EventConstants.CRISPR_REGION_UPDATE_FAILED:
      case EventConstants.CRISPR_REGION_DELETE_FAILED:
        this.regionLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_ORIGINAL_SEQUENCE_VERSION_FETCH_COMPLETED:
        this._updateOriginalSequenceVersion(action.analysisId, action.originalSequenceVersion);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_REGENERATE:
        this.regeneratingAnalysisXHRCount++;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ANALYSIS_REGENERATE_COMPLETED:
      case EventConstants.CRISPR_ANALYSIS_REGENERATE_FAILED:
        this.regeneratingAnalysisXHRCount--;
        this.emitChange();
        break;
    }
    return true;
  }
}

export default new CRISPRAnalysisStore(AppDispatcher);
